import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '@/i18n';
import api from '@utils/api';
import PageHeader from '@components/PageHeader';
import { IconGraduation, IconExternalLink } from '@components/icons';

const STATUS_CLS = {
  VALID:      'bg-green-50 text-green-700 border-green-200',
  WARNING_60: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  WARNING_30: 'bg-amber-50 text-amber-700 border-amber-200',
  WARNING_10: 'bg-orange-50 text-orange-700 border-orange-200',
  EXPIRED:    'bg-red-50 text-red-700 border-red-200',
};

function fmt(value, lang) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT');
}

function EmptyState({ children }) {
  return (
    <div className="card p-10 text-center">
      <div className="mx-auto w-12 h-12 grid place-items-center rounded-xl bg-gray-100 text-gray-400 mb-3">
        <IconGraduation size={24} />
      </div>
      <p className="text-sm text-gray-500">{children}</p>
    </div>
  );
}

// Shows the staff member's own training records inline, read straight from
// /training/summary (HR's server calls Training's internal API — no browser
// SSO round trip). The full Training platform is only entered on demand, via
// the same /auth/handoff ticket flow this page used to run unconditionally.
export default function Training() {
  const { t, lang } = useI18n();
  const [loading, setLoading]         = useState(true);
  const [linked, setLinked]           = useState(false);
  const [completions, setCompletions] = useState([]);
  const [opening, setOpening]         = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    api.get('/training/summary')
      .then(({ data }) => {
        setLinked(!!data.linked);
        setCompletions(data.completions || []);
      })
      .catch(() => setLinked(false))
      .finally(() => setLoading(false));
  }, []);

  const openFull = () => {
    setOpening(true);
    setError('');
    api.post('/auth/handoff', { aud: 'training' })
      .then(({ data }) => { window.location.href = data.redirectUrl; })
      .catch((err) => {
        setOpening(false);
        setError(err.response?.data?.message || t('training.error'));
      });
  };

  return (
    <>
      <PageHeader title={t('training.title')} subtitle={t('training.subtitle')} />
      <div className="px-5 md:px-0 pb-6 space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : !linked ? (
          <EmptyState>{t('training.notLinked')}</EmptyState>
        ) : completions.length === 0 ? (
          <EmptyState>{t('training.empty')}</EmptyState>
        ) : (
          <div className="space-y-2.5">
            {completions.map((c, i) => (
              <motion.div key={c.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="card p-4 flex items-center gap-4">
                <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-glk-blue/10 text-glk-blue">
                  <IconGraduation size={20} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-glk-dark truncate">{c.training}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {t('training.expires')} {fmt(c.expires_at, lang)}
                  </p>
                </div>
                <span className={`shrink-0 inline-block rounded border px-2 py-0.5 text-xs font-medium ${STATUS_CLS[c.warning_status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                  {t(`training.status.${c.warning_status}`)}
                </span>
              </motion.div>
            ))}
          </div>
        )}

        <button
          onClick={openFull}
          disabled={opening}
          className="btn-ghost w-full flex items-center justify-center gap-1.5"
        >
          {opening ? t('training.redirecting') : <>{t('training.openFull')} <IconExternalLink size={16} /></>}
        </button>
      </div>
    </>
  );
}
